import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

interface ShiprocketCheckoutOptions {
  redirectUrl?: string;
  fallbackUrl?: string;
}

declare global {
  interface Window {
    HeadlessCheckout?: {
      addToCart: (event: Event, token: string, options: { fallbackUrl: string }) => void;
    };
  }
}

export const useShiprocketCheckout = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { cartItems, clearCart } = useCart();

  // Load the Shiprocket checkout script
  const loadShiprocketScript = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.HeadlessCheckout) {
        resolve();
        return;
      }

      // Check if script tag already exists
      const existingScript = document.getElementById('shiprocket-checkout-script');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve());
        return;
      }

      // Add CSS
      if (!document.getElementById('shiprocket-checkout-css')) {
        const link = document.createElement('link');
        link.id = 'shiprocket-checkout-css';
        link.rel = 'stylesheet';
        link.href = 'https://checkout-ui.shiprocket.com/assets/styles/shopify.css';
        document.head.appendChild(link);
      }

      // Add hidden seller domain input (required by Shiprocket)
      if (!document.getElementById('sellerDomain')) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.value = window.location.hostname;
        input.id = 'sellerDomain';
        document.body.appendChild(input);
      }

      // Add script
      const script = document.createElement('script');
      script.id = 'shiprocket-checkout-script';
      script.src = 'https://checkout-ui.shiprocket.com/assets/js/channels/shopify.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Shiprocket checkout script'));
      document.body.appendChild(script);
    });
  }, []);

  // Cart item type for direct items passed from express checkout
  interface DirectCartItem {
    id: string;
    name?: string;
    price?: number;
    quantity: number;
    variantInfo?: {
      pot_type: string;
      pot_color: string;
      size: string;
    } | null;
  }

  // Generate checkout token and initiate Shiprocket checkout
  const initiateCheckout = useCallback(async (
    event: Event,
    directItems?: DirectCartItem[],
    options?: ShiprocketCheckoutOptions
  ) => {
    setLoading(true);
    setError(null);

    try {
      // Use direct items if provided (from Buy Now), otherwise use cart state
      const itemsToUse = directItems || cartItems;
      
      // Prepare cart items for Shiprocket with proper variant info
      const cartItemsForShiprocket = itemsToUse.map(item => ({
        product_id: item.id,
        pot_type: item.variantInfo?.pot_type || null,
        size: item.variantInfo?.size || null,
        pot_color: item.variantInfo?.pot_color || null,
        quantity: item.quantity
      }));

      if (cartItemsForShiprocket.length === 0) {
        throw new Error('Cart is empty');
      }

      console.log('Sending cart items to Shiprocket:', cartItemsForShiprocket);

      // Call our edge function to get the checkout token
      const { data, error: fnError } = await supabase.functions.invoke('shiprocket-checkout-token', {
        body: {
          cart_items: cartItemsForShiprocket,
          redirect_url: options?.redirectUrl || `${window.location.origin}/order-success`
        }
      });

      if (fnError || !data?.success) {
        throw new Error(data?.error || fnError?.message || 'Failed to generate checkout token');
      }

      const token = data.token;
      if (!token) {
        throw new Error('No token received from Shiprocket');
      }

      console.log('Shiprocket checkout token received:', token);

      // Load the Shiprocket script if not already loaded
      await loadShiprocketScript();

      // Initiate checkout using Shiprocket's HeadlessCheckout
      if (window.HeadlessCheckout) {
        window.HeadlessCheckout.addToCart(
          event, 
          token, 
          { fallbackUrl: options?.fallbackUrl || `${window.location.origin}/checkout` }
        );
        
        // Clear cart after successful checkout initiation
        // Note: Cart will be cleared only if checkout completes successfully via webhook
        toast.success('Opening Shiprocket Checkout...');
      } else {
        throw new Error('Shiprocket checkout script not loaded properly');
      }

    } catch (err: any) {
      console.error('Shiprocket checkout error:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to initiate Shiprocket checkout');
    } finally {
      setLoading(false);
    }
  }, [cartItems, loadShiprocketScript]);

  return {
    initiateCheckout,
    loading,
    error,
    loadShiprocketScript
  };
};
